import { Button, Modal, SkeletonText } from '@dev-spendesk/grapes';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { useAccountingSoftwareName } from 'src/core/modules/accounting-integration/apis';
import { useAnalyticalFieldMapping } from 'src/core/modules/accounting-integration/hooks/useAnalyticalFieldMapping';

import { FieldValuesMapping } from '../../../../../accounting-integration/components/AnalyticalFieldMapping/FieldValuesMapping/FieldValuesMapping';
import {
  type InternalValue,
  type AnalyticalFieldMappingFieldType,
} from '../../../accounting';

export const AnalyticalFieldMappingConfigurationModal = ({
  internalField,
  internalValues,
  targetValueId,
  fieldType,
  isModalOpen,
  closeModal,
}: {
  internalField: InternalValue;
  internalValues: InternalValue[];
  targetValueId?: string | undefined;
  fieldType: AnalyticalFieldMappingFieldType;
  isModalOpen: boolean;
  closeModal: () => void;
}) => {
  const { t } = useTranslation('global');
  const { accountingSoftwareName } = useAccountingSoftwareName();

  const {
    analyticalFieldMappings,
    isFieldMappingsLoading,
    isUpdating,
    isIntegrationNameLoading,
    syncMode,
    fieldMapping,
    fieldMappingValues,
    errors,
    setFieldMappingValues,
    setFieldMapping,
    onSaveChanges,
    handleValueValidation,
  } = useAnalyticalFieldMapping({
    field: internalField,
    values: targetValueId
      ? internalValues.filter(({ id }) => id === targetValueId)
      : internalValues,
    onUpdated: () => closeModal(),
    shouldValidateValues: true,
  });

  const isWorking = isFieldMappingsLoading || isUpdating;

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={closeModal}
      title={t(
        'bookkeep.integrations.settings.analyticalFieldMapping.modal.title',
        {
          integration: accountingSoftwareName,
          field: internalField.name,
        },
      )}
      subtitle={
        fieldMapping
          ? t(
              'bookkeep.integrations.settings.analyticalFieldMapping.modal.subTitle',
              {
                field: internalField.name,
                fieldName: fieldMapping.externalName,
                integration: accountingSoftwareName,
              },
            )
          : 'todo'
      }
      iconName="link"
      iconVariant="purple"
      actions={[
        <Button
          key="cancel"
          variant="secondaryNeutral"
          text={t('misc.cancel')}
          onClick={closeModal}
        />,
        <Button
          key="mapValue"
          variant="primaryBrand"
          text={t(
            'bookkeep.integrations.settings.analyticalFieldMapping.modal.mapValue',
          )}
          onClick={() => {
            if (fieldMapping) {
              onSaveChanges({
                mapping: {
                  fieldId: fieldMapping.id,
                  fieldType: fieldMapping.fieldType,
                  internalId: fieldMapping.internalId,
                  values: fieldMappingValues,
                },
              });
            }
          }}
        />,
      ]}
    >
      {isWorking && (
        <div data-testid="loading">
          <SkeletonText size="l" />
        </div>
      )}

      {!isWorking && (
        <FieldValuesMapping
          fieldType={fieldType}
          isIntegrationNameLoading={isIntegrationNameLoading}
          internalField={internalField}
          syncMode={syncMode}
          analyticalFieldMappings={analyticalFieldMappings}
          internalValues={internalValues}
          fieldMapping={fieldMapping}
          setFieldMapping={setFieldMapping}
          fieldMappingValues={fieldMappingValues}
          setFieldMappingValues={setFieldMappingValues}
          validateValues={() => {
            handleValueValidation();
          }}
          hasError={errors.valuesNotMapped.length > 0}
          hideFieldSelector
        />
      )}
    </Modal>
  );
};
